import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { LEAD_FORMS } from "./leadConfig";

const CONTACT_PREFS = [
  { v: "phone", l: "Telefonisch" },
  { v: "email", l: "E-mail" },
  { v: "whatsapp", l: "WhatsApp" },
];

const Label = ({ children }) => <label className="text-xs font-semibold text-slate-600">{children}</label>;
const Row = ({ children, span = 1 }) => (
  <div className={span === 2 ? "sm:col-span-2" : ""}>{children}</div>
);
const Select = (p) => (
  <select {...p} className={`w-full rounded-xl border border-border bg-white px-3 py-2 text-sm ${p.className || ""}`} />
);

// Render a single schema field.
const SchemaField = ({ field, value, onChange }) => {
  const common = { "data-testid": `lf-${field.key}` };
  const content = (() => {
    switch (field.type) {
      case "select":
        return (
          <Select value={value || ""} onChange={(e) => onChange(e.target.value)} {...common}>
            <option value="">Kies…</option>
            {(field.options || []).map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </Select>
        );
      case "textarea":
        return (
          <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            placeholder={field.placeholder || ""}
            className="w-full rounded-xl border border-border bg-white px-3 py-2 text-sm"
            {...common}
          />
        );
      case "checkbox":
        return (
          <label className="flex items-start gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-1" {...common} />
            <span>{field.label}</span>
          </label>
        );
      case "number":
        return <Input type="number" min="0" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || ""} className="rounded-xl" {...common} />;
      case "date":
        return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-xl" {...common} />;
      default:
        return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || ""} className="rounded-xl" {...common} />;
    }
  })();

  if (field.type === "checkbox") return <Row span={field.span || 2}>{content}</Row>;
  return (
    <Row span={field.span}>
      <Label>{field.label}</Label>
      <div className="mt-1">{content}</div>
    </Row>
  );
};

/**
 * Schema-driven lead form. Renders fields from LEAD_FORMS[type]. Posts to
 * /api/vastgoed-leads with common contact fields at root and all type-specific
 * answers packed into `type_data`.
 */
export default function LeadForm({ type, onClose, onSuccess }) {
  const schema = LEAD_FORMS[type];
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    postal_code: "", house_number: "", city: "",
    contact_preference: "phone",
    consent_partners: false,
    notes: "",
    type_data: {},
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTd = (k, v) => setForm((f) => ({ ...f, type_data: { ...f.type_data, [k]: v } }));

  const showAddress = schema?.address || "none";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Naam en e-mailadres zijn verplicht");
      return;
    }
    if (!form.consent_partners) {
      toast.error("Geef toestemming om door een partner benaderd te worden");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        lead_type: type,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        postal_code: form.postal_code.trim() || null,
        house_number: form.house_number.trim() || null,
        city: form.city.trim() || null,
        contact_preference: form.contact_preference,
        consent_partners: !!form.consent_partners,
        notes: form.notes.trim() || null,
        source_screen: typeof window !== "undefined" ? window.location.pathname : null,
        type_data: form.type_data,
      };
      try {
        const qs = new URLSearchParams(window.location.search);
        payload.utm_source = qs.get("utm_source") || null;
        payload.utm_medium = qs.get("utm_medium") || null;
        payload.utm_campaign = qs.get("utm_campaign") || null;
      } catch { /* ignore */ }

      await api("/vastgoed-leads", { method: "POST", body: JSON.stringify(payload) });
      setDone(true);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.message || "Verzenden mislukt");
    } finally {
      setBusy(false);
    }
  };

  const addressBlock = useMemo(() => {
    if (showAddress === "none") return null;
    if (showAddress === "city_only") {
      return (
        <Row span={2}>
          <Label>Plaats</Label>
          <div className="mt-1"><Input value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-xl" data-testid="lf-city" /></div>
        </Row>
      );
    }
    return (
      <>
        <Row>
          <Label>Postcode</Label>
          <div className="mt-1"><Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="rounded-xl" placeholder="1234AB" data-testid="lf-postal" /></div>
        </Row>
        <Row>
          <Label>Huisnummer</Label>
          <div className="mt-1"><Input value={form.house_number} onChange={(e) => set("house_number", e.target.value)} className="rounded-xl" data-testid="lf-housenr" /></div>
        </Row>
        <Row span={2}>
          <Label>Plaats</Label>
          <div className="mt-1"><Input value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-xl" data-testid="lf-city" /></div>
        </Row>
      </>
    );
  }, [showAddress, form.postal_code, form.house_number, form.city]);

  if (!schema) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-navy/70 p-4 pt-10 backdrop-blur-sm" role="dialog">
      <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl" data-testid={`lead-form-${type}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-extrabold text-navy">{schema.modalTitle}</h2>
            <p className="mt-1 text-sm text-slate-500">{schema.modalSubtitle}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Sluiten" className="rounded-full p-1 hover:bg-slate-100" data-testid="lead-close">
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="mt-6 flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 size={48} className="text-success" />
            <h3 className="font-display text-lg font-bold text-navy">{schema.successTitle}</h3>
            <p className="text-sm text-slate-500">{schema.successBody}</p>
            <Button className="mt-3 rounded-xl" onClick={onClose} data-testid="lead-done">Sluiten</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Row>
              <Label>Naam *</Label>
              <div className="mt-1"><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl" required data-testid="lf-name" /></div>
            </Row>
            <Row>
              <Label>E-mailadres *</Label>
              <div className="mt-1"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-xl" required data-testid="lf-email" /></div>
            </Row>
            <Row>
              <Label>Telefoonnummer</Label>
              <div className="mt-1"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-xl" data-testid="lf-phone" /></div>
            </Row>
            <Row>
              <Label>Contactvoorkeur</Label>
              <div className="mt-1">
                <Select value={form.contact_preference} onChange={(e) => set("contact_preference", e.target.value)} data-testid="lf-contact-pref">
                  {CONTACT_PREFS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </Select>
              </div>
            </Row>

            {addressBlock}

            {schema.fields.map((f) => (
              <SchemaField
                key={f.key}
                field={f}
                value={form.type_data[f.key]}
                onChange={(v) => setTd(f.key, v)}
              />
            ))}

            <Row span={2}>
              <Label>Toelichting (optioneel)</Label>
              <div className="mt-1">
                <textarea
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  className="w-full rounded-xl border border-border bg-white px-3 py-2 text-sm"
                  rows={3}
                  data-testid="lf-notes"
                />
              </div>
            </Row>

            <label className="flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-600 sm:col-span-2">
              <input
                type="checkbox"
                checked={form.consent_partners}
                onChange={(e) => set("consent_partners", e.target.checked)}
                className="mt-1"
                data-testid="lf-consent"
                required
              />
              <span>
                Ik geef toestemming dat WoningAlert mijn gegevens deelt met één of meerdere geselecteerde vastgoedpartners die contact met mij mogen opnemen.
              </span>
            </label>

            <div className="sm:col-span-2">
              <Button type="submit" disabled={busy || !form.consent_partners} className="w-full rounded-xl bg-navy text-amber-300 hover:bg-navy/90" data-testid="lf-submit">
                {busy ? <Loader2 className="animate-spin" size={16} /> : schema.submitLabel}
              </Button>
              <p className="mt-2 text-center text-[11px] text-slate-400">
                100% gratis, vrijblijvend. Je gegevens worden alleen gedeeld met partners die relevant zijn.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
